import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-hot-toast";
import ButtonComponent from "../../../Ui/Button";
import Modal from "../../../Ui/Modal";
import { Formik, Form } from "formik";
import FormField from "../../VendorOnboarding/FormField";
import * as Yup from "yup";
import { familyMemberFieldConfig } from "../../../constant/Home/Properties";

const AddFamilyMember = ({
  setModalState,

  setEditProperty,
  onSuccess,
}) => {
  const formikSubmitRef = useRef(null);
  const currentFormValuesRef = useRef({});

  const [familyMembers, setFamilyMembers] = useState([
    { id: Date.now(), name: "", email: "" },
  ]);

  // validation schema
  const createValidationSchema = () => {
    const schemaFields = {};
    familyMembers.forEach((member, memberIndex) => {
      familyMemberFieldConfig.forEach((field) => {
        const fieldName = `member_${member.id}_${field.name}`;
        if (field.name === "name") {
          schemaFields[fieldName] = Yup.string()
            .trim()
            .required("Family member name is required");
        } else if (field.name === "email") {
          schemaFields[fieldName] = Yup.string()
            .email("Invalid email format")
            .trim()
            .required("Family member email is required");
        }
      });
    });
    return Yup.object().shape(schemaFields);
  };

  const addFamilyMember = (setFieldValue) => {
    const newId = Date.now();

    // Store current form values before adding new family member
    const currentValues = { ...currentFormValuesRef.current };

    setFamilyMembers((prev) => [...prev, { id: newId, name: "", email: "" }]);

    // Initialize new fields in Formik
    familyMemberFieldConfig.forEach((field) => {
      setFieldValue(`member_${newId}_${field.name}`, "");
    });

    // Restore previous form values
    Object.keys(currentValues).forEach((fieldName) => {
      if (currentValues[fieldName] !== undefined) {
        setFieldValue(fieldName, currentValues[fieldName]);
      }
    });
  };

  const removeFamilyMember = (id) => {
    if (familyMembers.length > 1) {
      setFamilyMembers((prev) => prev.filter((member) => member.id !== id));
    }
  };

  const handleSubmit = (values) => {
    const loadingToastId = toast.loading("Adding family members...");

    const extractedFamilyMembers = familyMembers.map((member) => ({
      name: values[`member_${member.id}_name`] || "",
      email: values[`member_${member.id}_email`] || "",
    }));

    const payload = {
      familyMembers: extractedFamilyMembers.map((member) => ({
        name: member.name.trim(),
        email: member.email.trim(),
      })),
    };

    // Close loading toast
    toast.dismiss(loadingToastId);

    // Show success message
    toast.success("Family members added successfully!");

    // Close modal
    setModalState("familyInvitation");

    // Call onSuccess callback if provided
    if (onSuccess) {
      onSuccess(payload);
    }
  };

  const handleClose = () => {
    setEditProperty && setEditProperty(null);
    setModalState("");
  };

  const getInitialValues = () => {
    const values = {};
    familyMembers.forEach((member) => {
      familyMemberFieldConfig.forEach((field) => {
        const value = member[field.name];
        values[`member_${member.id}_${field.name}`] =
          typeof value === "string" ? value : "";
      });
    });
    return values;
  };

  return (
    <Modal
      open={true}
      onClose={handleClose}
      title="Add Family Member"
      footer={
        <div className="flex w-full gap-4 justify-end">
          <ButtonComponent paddingY="10px" onClick={() => setModalState("")}>
            Cancel
          </ButtonComponent>
          <ButtonComponent
            bgColor="#3CAB5C"
            color="#FFFFFF"
            paddingY="10px"
            onClick={() => formikSubmitRef.current && formikSubmitRef.current()}
          >
            Save
          </ButtonComponent>
        </div>
      }
    >
      <Formik
        initialValues={getInitialValues()}
        enableReinitialize={false}
        validationSchema={createValidationSchema()}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, submitForm, errors, touched, values }) => {
          formikSubmitRef.current = submitForm;
          currentFormValuesRef.current = values;

          return (
            <Form>
              <div>
                {familyMembers.map((member) => (
                  <div key={member.id} className="p-4">
                    <div className="flex justify-between items-center mb-4">
                      {familyMembers.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeFamilyMember(member.id)}
                          className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-1 gap-4 [&>*:nth-child(n+3)]:md:col-span-2">
                      {familyMemberFieldConfig.map((field, fieldIndex) => (
                        <div key={fieldIndex}>
                          <FormField
                            label={field.label}
                            name={`member_${member.id}_${field.name}`}
                            type={field.type}
                            placeholder={field.placeholder}
                            options={field.options}
                            error={errors[`member_${member.id}_${field.name}`]}
                            touched={
                              touched[`member_${member.id}_${field.name}`]
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="flex text-nowrap justify-center mb-4">
                  <ButtonComponent
                    color="#52C171"
                    bgColor="#52C1711A"
                    borderColor="#3CAB5C"
                    paddingY="10px"
                    type="button"
                    onClick={() => addFamilyMember(setFieldValue)}
                  >
                    Add Additional Family Member
                  </ButtonComponent>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AddFamilyMember;
